import { useState, useEffect, useRef } from "react";

// Screen size breakpoints (in pixels)
export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

export type Breakpoint = keyof typeof breakpoints;

// Screen size interface
export interface ScreenSize {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  breakpoint: Breakpoint;
  availableWidth: number;
}

/**
 * Simple hook to get current window width and listen for resize events
 * @returns Current window width
 */
export function useWindowWidth(): number {
  const [width, setWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWidth(window.innerWidth);
      if (process.env.NODE_ENV === "development") {
      }
    };

    // Set initial size and log it
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

/**
 * Hook to get a container element's width
 * @param parentSelector - CSS selector for the parent container to match width with
 * @returns Container width and a ref to attach to your component
 */
export function useContainerWidth(parentSelector?: string): {
  containerWidth: number;
  ref: React.RefObject<HTMLElement>;
} {
  const ref = useRef<HTMLElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateWidth = () => {
      // If parent selector is provided, try to match that width
      if (parentSelector) {
        const parentElement = document.querySelector(
          parentSelector
        ) as HTMLElement;
        if (parentElement) {
          setContainerWidth(parentElement.offsetWidth);
          return;
        }
      }

      // Otherwise use our own container's width
      if (ref.current) {
        const parent = ref.current.parentElement;
        setContainerWidth(parent?.offsetWidth || 0);
      }
    };

    // Initial measurement
    updateWidth();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateWidth);

    // Observe parent element if specified
    if (parentSelector) {
      const parentElement = document.querySelector(parentSelector);
      if (parentElement) {
        resizeObserver.observe(parentElement);
      }
    }

    // Also observe our container's parent
    if (ref.current?.parentElement) {
      resizeObserver.observe(ref.current.parentElement);
    }

    // Set up window resize listener as fallback
    window.addEventListener("resize", updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [parentSelector]);

  return { containerWidth, ref };
}

/**
 * Hook to get and observe screen size changes
 * @param containerRef - Optional ref to a container element to get its width instead of window width
 * @returns Current screen dimensions and breakpoint information
 */
export function useScreenSize(
  containerRef?: React.RefObject<HTMLElement>
): ScreenSize {
  // Default to window dimensions
  const getSize = (): ScreenSize => {
    // Handle SSR case where window might not be available
    const width = typeof window !== "undefined" ? window.innerWidth : 0;
    const height = typeof window !== "undefined" ? window.innerHeight : 0;

    // Get container width if ref is provided and valid
    const containerWidth = containerRef?.current?.clientWidth;
    const availableWidth = containerWidth || width;

    // Determine current breakpoint
    let currentBreakpoint: Breakpoint = "xs";
    Object.entries(breakpoints).forEach(([breakpoint, minWidth]) => {
      if (width >= minWidth) {
        currentBreakpoint = breakpoint as Breakpoint;
      }
    });

    return {
      width,
      height,
      isMobile: width < breakpoints.md,
      isTablet: width >= breakpoints.md && width < breakpoints.lg,
      isDesktop: width >= breakpoints.lg,
      breakpoint: currentBreakpoint,
      availableWidth,
    };
  };

  const [screenSize, setScreenSize] = useState<ScreenSize>(getSize());

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setScreenSize(getSize());
    };

    // Set initial size
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up
    return () => window.removeEventListener("resize", handleResize);
  }, [containerRef]);

  return screenSize;
}

/**
 * Calculate the optimal carousel width based on screen size
 * @param screenSize - The current screen size information
 * @param maxItems - Optional maximum number of items to display
 * @param itemWidth - Width of each carousel item in pixels
 * @param gap - Gap between items in pixels
 * @returns The optimal width for the carousel in pixels
 */
export function getOptimalCarouselWidth(
  screenSize: ScreenSize,
  maxItems: number = 3,
  itemWidth: number = 280,
  gap: number = 16
): number {
  // Calculate how many items can fit
  const maxWidth = screenSize.availableWidth * 0.95; // 95% of available width
  const itemTotalWidth = itemWidth + gap;

  // Calculate how many items can fit
  const itemsFit = Math.min(Math.floor(maxWidth / itemTotalWidth), maxItems);

  // Calculate the optimal width based on the number of items
  return Math.min(
    itemsFit * itemTotalWidth,
    maxItems * itemTotalWidth,
    maxWidth
  );
}
